import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { MUNICIPIOS_IDS } from '../constants/municipios'
import { ComponentRenderer } from '../components/ComponentRenderer'
import { useTrackDashboardView } from '../hooks/useTrackDashboardView'
import logoBMP from '../assets/logos/BMP.jpg'
import logoFinanciadores from '../assets/logos/FINANCIA_ON_line_BLANCO.png'
import DatosGenerales from '../components/generic/DatosGenerales'
import DatosTematicos from '../components/generic/DatosTematicos'
import DatosForestales from '../components/generic/DatosForestales'
import Visor from '../components/generic/Visor'
import Certificacion from '../components/generic/Certificacion'
//import { DashboardView } from '../components/generic/DashboardView'

interface DashboardComponent {
  id: string
  position: number
  grid_column: number
  grid_row: number
  grid_x: number
  grid_y: number
  components: {
    id: string
    name: string
    type: string
    configuration: any
  }
}

interface Dashboard {
  id: number
  name: string
  slug?: string
  description: string | null
  dashboard_components: DashboardComponent[]
}

export function LandingPage() {
  const [view, setView] = useState<'generic' | 'tematics' | 'forestry' | 'visor' | 'certification' | 'dashboard'>('generic')
  const [dashboard, setDashboard] = useState<any | null>(null)
  const [municipios, setMunicipios] = useState<any[]>([])
  const [selectedMunicipio, setSelectedMunicipio] = useState('')
  const [loadingDashboard, setLoadingDashboard] = useState(false)
  const [tematicsActiveCategory, setTematicsActiveCategory] = useState<any | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  // Estado para recordar la categoría y subcategoría seleccionada en DatosGenerales
  const [generalesSelectedCategory, setGeneralesSelectedCategory] = useState<any | null>(null)
  const [generalesSelectedSubCategory, setGeneralesSelectedSubCategory] = useState<string | null>(null)

  // Track dashboard views si tienes ese hook
  useTrackDashboardView(dashboard?.id)

  useEffect(() => {
    loadMunicipios()
  }, [])

  // Capturar parámetro dashboard de la URL al cargar la página
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const dashboardSlug = params.get('dashboard')
    
    if (dashboardSlug) {
      openDashboardBySlug(dashboardSlug)
    }
  }, [])

  const loadMunicipios = async () => {
    try {
      const { data, error } = await supabase
        .from('municipios')
        .select('id_municipio, nombre')
        .in('id_municipio', MUNICIPIOS_IDS)

      if (error) throw error

      console.log("data", data);
      setMunicipios(data || [])
    } catch (err) {
      console.error('Error cargando municipios:', err)
      setMunicipios([])
    }
  }

  // Abre un dashboard solicitando los datos completos a supabase por id (no toca la URL)
  const openDashboardById = async (id: number) => {
    try {
      setLoadingDashboard(true)

      const { data, error } = await supabase
        .from('dashboards')
        .select(`
          id,
          name,
          slug,
          description,
          dashboard_components (
            id,
            position,
            grid_column,
            grid_row,
            grid_x,
            grid_y,
            components (
              id,
              name,
              type,
              configuration
            )
          )
        `)
        .eq('id', id)
        .single()

      if (error) {
        throw error
      }

       const dashboardData = data as any

      setDashboard(dashboardData as Dashboard)
      setView('dashboard')
      setMenuOpen(false)
      
      // Actualizar URL con el slug
      if (dashboardData.slug) {
        window.history.pushState({}, '', `?dashboard=${dashboardData.slug}`)
      }
    } catch (err) {
      console.error('Error cargando dashboard:', err)
    } finally {
      setLoadingDashboard(false)
    }
  }

  // Nueva función para abrir dashboard por slug desde URL
  const openDashboardBySlug = async (slug: string) => {
    try {
      setLoadingDashboard(true)

      const { data, error } = await supabase
        .from('dashboards')
        .select(`
          id,
          name,
          slug,
          description,
          dashboard_components (
            id,
            position,
            grid_column,
            grid_row,
            grid_x,
            grid_y,
            components (
              id,
              name,
              type,
              configuration
            )
          )
        `)
        .eq('slug', slug)
        .single()

      if (error) {
        throw error
      }

      setDashboard(data as Dashboard)
      setView('dashboard')
      setMenuOpen(false)
      
      // Ya está en la URL, no es necesario cambiar
    } catch (err) {
      console.error('Error cargando dashboard por slug:', err)
      // Si falla, redirigir a la vista genérica
      setView('generic')
      window.history.pushState({}, '', window.location.pathname)
    } finally {
      setLoadingDashboard(false)
    }
  }

  const backToCategories = () => {
    // Volvemos a la vista de Datos Generales manteniendo la categoría seleccionada
    setDashboard(null)
    setView('generic')
    setMenuOpen(true)
    // Limpiar parámetro de la URL
    window.history.pushState({}, '', window.location.pathname)
  }

  // Función para manejar cuando se selecciona un dashboard desde DatosGenerales
  const handleSelectDashboardFromGenerales = (id: number) => {
    openDashboardById(id)
  }

  // Sorted components (cuando haya dashboard)
  const sortedComponents = [...(dashboard?.dashboard_components || [])].sort((a, b) => a.position - b.position)

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="w-full px-4 py-4 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* LEFT: logo + title */}
          <div className="flex items-center gap-3">
            <img src={logoBMP} alt="Bosque Modelo Palencia" className="h-10 md:h-12 w-auto object-contain" />
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-bmp-black">MONBIOFOR</h1>
              <p className="text-sm text-gray-600">Panel de Administración</p>
            </div>
          </div>

          {/* RIGHT: desktop nav + mobile hamburger button */}
          <div className="flex items-center gap-3">
            <nav className="hidden lg:flex gap-4 items-center">
              <button onClick={() => { 
                setView('generic'); 
                setDashboard(null); 
                setGeneralesSelectedCategory(null);
                setGeneralesSelectedSubCategory(null);
                window.history.pushState({}, '', window.location.pathname) 
              }}
                className={`px-3 py-2 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-medium transition-colors ${view === 'generic' ? 'bg-bmp-green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                Catálogos de datos
              </button>

              <button onClick={() => { setView('tematics'); setDashboard(null); window.history.pushState({}, '', window.location.pathname) }}
                className={`px-3 py-2 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-medium transition-colors ${view === 'tematics' ? 'bg-bmp-green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                Fichas temáticas
              </button>

              <button onClick={() => { setView('forestry'); setDashboard(null); window.history.pushState({}, '', window.location.pathname) }}
                className={`px-3 py-2 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-medium transition-colors ${view === 'forestry' ? 'bg-bmp-green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                Fichas por especies
              </button>

              <button onClick={() => { setView('visor'); setDashboard(null); window.history.pushState({}, '', window.location.pathname) }}
                className={`px-3 py-2 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-medium transition-colors ${view === 'visor' ? 'bg-bmp-green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                Visor
              </button>

              <button onClick={() => { setView('certification'); setDashboard(null); window.history.pushState({}, '', window.location.pathname) }}
                className={`px-3 py-2 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-medium transition-colors ${view === 'certification' ? 'bg-bmp-green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                Certificación
              </button>
            </nav>

            {/* Hamburger button (mobile) */}
            <button onClick={() => setMenuOpen(v => !v)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition" aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"} aria-expanded={menuOpen}>
              <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile menu */}
          {menuOpen && (
            <div className="lg:hidden mt-3 rounded-xl bg-white shadow-lg border border-gray-200 w-full">
              <nav className="flex flex-col divide-y">
                <button onClick={() => { 
                  setView('generic'); 
                  setDashboard(null); 
                  setGeneralesSelectedCategory(null);
                  setGeneralesSelectedSubCategory(null);
                  setMenuOpen(false); 
                  window.history.pushState({}, '', window.location.pathname) 
                }} className="px-4 py-3 text-left hover:bg-gray-50">Catálogos de datos</button>
                <button onClick={() => { setView('tematics'); setDashboard(null); setMenuOpen(false); window.history.pushState({}, '', window.location.pathname) }} className="px-4 py-3 text-left hover:bg-gray-50">Fichas temáticas</button>
                <button onClick={() => { setView('forestry'); setDashboard(null); setMenuOpen(false); window.history.pushState({}, '', window.location.pathname) }} className="px-4 py-3 text-left hover:bg-gray-50">Fichas por especies</button>
                <button onClick={() => { setView('visor'); setDashboard(null); setMenuOpen(false); window.history.pushState({}, '', window.location.pathname) }} className="px-4 py-3 text-left hover:bg-gray-50">Visor</button>
                <button onClick={() => { setView('certification'); setDashboard(null); setMenuOpen(false); window.history.pushState({}, '', window.location.pathname) }} className="px-4 py-3 text-left bg-bmp-green text-white hover:bg-bmp-green/90">Certificación</button>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* CONTENIDO */}
      <div className="flex-1">
        {view === 'generic' && (
          <DatosGenerales 
            onSelectDashboard={handleSelectDashboardFromGenerales}
            selectedCategory={generalesSelectedCategory}
            selectedSubCategory={generalesSelectedSubCategory}
            onSelectCategory={setGeneralesSelectedCategory}
            onSelectSubCategory={setGeneralesSelectedSubCategory}
          />
        )}

        {view === 'tematics' && (
          <DatosTematicos
            activeCategory={tematicsActiveCategory}
            onSelectCategory={(cat) => {
              setTematicsActiveCategory(cat)
              // Aquí NO llamamos a openDashboardById directamente
              // El componente DatosTematicos se encargará de llamar a onSelectDashboard
            }}
            /*onSelectDashboard={(d:any) => {
              // Esta función se llama desde DatosTematicos cuando encuentra el dashboard
              openDashboardById(d.id)
            }}*/
          />
        )}

        {view === 'forestry' && <DatosForestales />}
        {view === 'visor' && <Visor height="70vh" sidebarVisible={true} />}
        {view === 'certification' && <Certificacion />}

        {view === 'dashboard' && dashboard && (
          <>
            {/* Back button + filter municipio */}
            <main className="container mx-auto px-4 py-8 flex-1">
              <div className="items-center justify-between ">
                <button
                  onClick={() => backToCategories()}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline mb-2 inline-flex items-center gap-2 text-teal-700 font-medium hover:underline"
                >
                  ← Volver a subcategoría
                </button>
                <br />
                <br />
              </div>

              <div className="w-full max-w-sm">
                <label className="block text-sm font-medium text-gray-700 mb-1">Filtra por municipio</label>
                <div className="relative">
                  <select value={selectedMunicipio} onChange={e => setSelectedMunicipio(e.target.value)} className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Todos los municipios</option>
                    {municipios?.map((m: any) => <option key={m.id_municipio} value={m.id_municipio}>{m.nombre}</option>)}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">▼</span>
                </div>
              </div>

              <hr />
              <br />

              <div className="mx-auto" style={{ maxWidth: '1400px' }}>
                {loadingDashboard ? (
                  <div className="min-h-40 flex items-center justify-center">Cargando dashboard...</div>
                ) : sortedComponents.length === 0 ? (
                  <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                    <div className="text-6xl mb-4">📦</div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">Dashboard vacío</h3>
                    <p className="text-gray-500">Este dashboard no tiene componentes aún.</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop: grid 12 cols */}
                    <div className="hidden lg:grid gap-4" style={{ gridTemplateColumns: 'repeat(12, 1fr)', gridAutoRows: '120px' }}>
                      {sortedComponents.map((dashComp) => {
                        const c = dashComp.components
                        return (
                          <div key={dashComp.id} style={{ gridColumn: `${dashComp.grid_x + 1} / span ${dashComp.grid_column}`, gridRow: `${dashComp.grid_y + 1} / span ${dashComp.grid_row}` }}>
                            <ComponentRenderer 
                              component={{ 
                                    id: c.id, 
                                    name: c.name, 
                                    type: c.type as any, 
                                    configuration: {
                                      ...c.configuration, // Mantener la configuración original
                                      filterMunicipio: selectedMunicipio || null,
                                    }, 
                                  }
                                } 
                                filterMunicipio={selectedMunicipio} 
                                filterMunicipioNombre={selectedMunicipio ? 
                                    municipios.find(m => m.id_municipio == selectedMunicipio)?.nombre || null 
                                    : null}
                                />
                          </div>
                        )
                      })}
                    </div>

                    {/* Mobile: vertical */}
                    <div className="flex flex-col gap-6 lg:hidden">
                      {sortedComponents.map((dashComp) => {
                        const c = dashComp.components
                        return <div key={dashComp.id} className="w-full">
                          <ComponentRenderer 
                            component={{ 
                              id: c.id, 
                              name: c.name, 
                              type: c.type as any, 
                              configuration: {
                                ...c.configuration, // Mantener la configuración original
                                filterMunicipio: selectedMunicipio || null,
                              },
                            }}
                            filterMunicipio={selectedMunicipio} 
                            filterMunicipioNombre={selectedMunicipio ? 
                                    municipios.find(m => m.id_municipio == selectedMunicipio)?.nombre || null 
                                    : null}
                            />
                            </div>
                      })}
                    </div>
                  </>
                )}
              </div>
              <br /><br />
            </main>
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-bmp-black border-t border-gray-800 mt-auto">
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center mb-6">
            <img src={logoFinanciadores} alt="Financiadores" className="h-16 md:h-20 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity" />
          </div>
          <div className="text-center text-gray-400 text-sm space-y-2">
            <div className="pt-4 border-t border-gray-800 mt-4">
              <p className="text-gray-400">Contacto: <a href="mailto:secretariatecnica@palenciabosquemodelo.es" className="text-bmp-purple-light hover:text-bmp-purple transition-colors underline">secretariatecnica@palenciabosquemodelo.es</a></p>
            </div>
            <p className="text-xs text-gray-600 pt-4">Sistema de visualización de datos forestales y ambientales © {new Date().getFullYear()}</p>
          </div>
        </div>
      </footer>
    </div>
  )
}